import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { faGear, faPowerOff } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import List, { type ListItem } from "../../../components/List/List"
import { ButtonIcon } from "../../../components/Button/Button"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { useRodizio } from "../../../hooks/Rodizio/useRodizio"
import type { RodizioSettings } from "../../../Objects/Rodizio/RodizioSettings"
import type { RodizioPlan } from "../../../Objects/Rodizio/RodizioPlan"
import type { RodizioManagement } from "../../../Objects/Rodizio/RodizioManagement"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { RodizioPlanPopup } from "./RodizioPlanPopup"
import { RodizioSettingsPopup } from "./RodizioSettingsPopup"
import GetSpace from "../../../utils/space"

export const RodizioPage: React.FC = () => {
  const { t } = useTranslation(["rodizio", "list"])

  const api = useRodizio()

  const apiRef = useRef(api)

  const { setNewConfirmation } = useConfirmation()

  const quickActionHandledRef = useRef(false)

  const [searchParams] = useSearchParams()

  const [management, setManagement] = useState<RodizioManagement | null>(null)

  const [editingPlan, setEditingPlan] = useState<RodizioPlan | undefined>()

  const [showPlan, setShowPlan] = useState(false)

  const [showSettings, setShowSettings] = useState(false)

  useEffect(() => {
    apiRef.current = api
  }, [api])

  const handleFinishAllClick = useCallback(() => {
    setNewConfirmation(t("rodizio:page.finish_all_title"), t("rodizio:page.finish_all_message"), () => {
      void (async () => {
        const finishedCount = await apiRef.current.finishAllActiveSessions()

        if (finishedCount === undefined) return

        setNewConfirmation(
          t("rodizio:page.finish_all_title"),
          finishedCount > 0
            ? t("rodizio:page.finish_all_success", { count: finishedCount })
            : t("rodizio:page.finish_all_none"),
          () => {},
        )
      })()
    })
  }, [t])

  const load = useCallback(async () => {
    const configuration = await apiRef.current.getManagement()

    if (configuration) setManagement(configuration)

    if (configuration && !quickActionHandledRef.current) {
      quickActionHandledRef.current = true

      const action = searchParams.get("quickAction")

      if (action === "rodizio-plan-create") {
        setEditingPlan(undefined)

        setShowPlan(true)
      }

      if (action === "rodizio-settings") setShowSettings(true)

      if (action === "rodizio-finish-all") handleFinishAllClick()
    }
  }, [searchParams, handleFinishAllClick])

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0)

    return () => window.clearTimeout(timer)
  }, [load])

  const cards = useMemo<ListItem[]>(
    () =>
      (management?.plans ?? []).map((plan, index) => ({
        id: plan.id,
        index,
        title: plan.name,
        linkImage: "",
        isDisable: !plan.isActive,
        dataFilter: [plan.name, plan.description ?? "", plan.chargeProductName],
        children: (
          <div
            className={`rounded-2xl border p-4 ${plan.isActive ? "border-orange-100 bg-white" : "border-slate-200 bg-slate-100 opacity-70"}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-base font-black text-slate-900">{plan.name}</p>

                <p className="mt-1 text-sm font-semibold text-slate-600">
                  {plan.description || t("rodizio:plan.no_description")}
                </p>
              </div>

              <span className="rounded-full bg-orange-100 px-3 py-1 text-sm font-black text-orange-700">
                {plan.price.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
              </span>
            </div>

            <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold">
              <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                {t("rodizio:plan.products_count", { count: plan.productIds.length })}
              </span>

              <span className="rounded-full bg-blue-50 px-3 py-1 text-blue-700">
                {t("rodizio:plan.charge_reference", { product: plan.chargeProductName })}
              </span>

              <span
                className={`rounded-full px-3 py-1 ${plan.isActive ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"}`}
              >
                {plan.isActive ? t("rodizio:common.active") : t("rodizio:common.inactive")}
              </span>
            </div>
          </div>
        ),
      })),
    [management, t],
  )

  const handleCopyPlan = () => {
    if (!editingPlan) {
      return
    }

    setEditingPlan({ ...editingPlan, id: 0, productIds: [...editingPlan.productIds] })
  }

  return (
    <>
      <div className={`${defaultStyle.containerList} space-y-4`}>
        <List
          enableLocalPagination
          messageCount={t("rodizio:plan.count")}
          messageEmpty={t("rodizio:plan.empty")}
          nameFindSearch={t("rodizio:plan.search")}
          data={cards}
          handleAddButton={() => {
            setEditingPlan(undefined)
            setShowPlan(true)
          }}
          handleRefreshButton={load}
          handleExtraFloatButton={() => setShowSettings(true)}
          extraFloatButtonIcon={faGear}
          extraFloatButtonDescriptionKey="interactionDescriptions:rodizio.page.settings"
          filterActions={
            <>
              <ButtonIcon
                sizeNumber={SIZE.buttonCloseIconSize}
                bgColor={THEME.colors.buttonBGRemoveColor}
                iconColor={THEME.colors.buttonFGRemoveColor}
                icon={faPowerOff}
                onClick={handleFinishAllClick}
                ariaLabel={t("rodizio:page.finish_all")}
                descriptionKey="interactionDescriptions:rodizio.page.finishAll"
              />

              <GetSpace left={5} />

              <PopupHelpButton
                helpKey="rodizio"
                variant="global"
                title={t("helpPopup:global.rodizio.title")}
                description={t("helpPopup:global.rodizio.description")}
                steps={t("helpPopup:global.rodizio.steps", { returnObjects: true }) as string[]}
                bullets={t("helpPopup:global.rodizio.bullets", { returnObjects: true }) as string[]}
              />
            </>
          }
          handleOnClick={(id) => {
            setEditingPlan(management?.plans.find((plan) => plan.id === id))
            setShowPlan(true)
          }}
        />
      </div>

      {showSettings && management && (
        <RodizioSettingsPopup
          settings={management.settings}
          isLoading={api.isLoading}
          onClose={() => setShowSettings(false)}
          onSave={async (settings: RodizioSettings) => {
            const saved = await api.saveSettings(settings)
            if (!saved) return false
            setManagement((current) => (current ? { ...current, settings: saved } : current))
            return true
          }}
        />
      )}

      {showPlan && (
        <RodizioPlanPopup
          plan={editingPlan}
          isLoading={api.isLoading}
          onClose={() => setShowPlan(false)}
          onCopy={handleCopyPlan}
          onSave={async (plan) => {
            const saved = await api.savePlan(plan)
            if (!saved) return false
            await load()
            return true
          }}
          onToggleStatus={async (plan) => {
            const saved = await api.setPlanStatus(plan.id, !plan.isActive)
            if (!saved) return false
            await load()
            return true
          }}
          onRemove={async (plan) => {
            const removed = await api.removePlan(plan.id)
            if (!removed) return false
            await load()
            return true
          }}
        />
      )}
    </>
  )
}
